/**
 * Smoke Drive share gates — expiry, download cap, named staff, password page gate.
 * Password is enforced on the branded page only; current Console rules still
 * expose the share document to anyone who can read it. Do not use password for
 * highly sensitive files until a Cloud Run gate exists.
 */

function toMillis(value) {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    if (value instanceof Date) return value.getTime();
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

export const DRIVE_SHARE_EXPIRY_PRESETS = [
    { id: 'never', label: 'No expiry', hours: 0 },
    { id: '1d', label: '1 day', hours: 24 },
    { id: '7d', label: '7 days', hours: 24 * 7 },
    { id: '30d', label: '30 days', hours: 24 * 30 },
];

export function expiryDateFromPreset(presetId, nowMs = Date.now()) {
    const preset = DRIVE_SHARE_EXPIRY_PRESETS.find((p) => p.id === presetId);
    if (!preset || !preset.hours) return null;
    return new Date(nowMs + preset.hours * 60 * 60 * 1000);
}

export function shareHasAccessGates(share) {
    if (!share) return false;
    if (share.passwordProtected) return true;
    if (toMillis(share.expiresAt) > 0) return true;
    if ((Number(share.maxDownloads) || 0) > 0) return true;
    if (Array.isArray(share.memberUids) && share.memberUids.length > 0) return true;
    return false;
}

export function shareHasExtraGates(share) {
    if (shareHasAccessGates(share)) return true;
    if (share?.itemType === 'folder') return true;
    return false;
}

export function shareOptionsHaveGates(options) {
    if (!options) return false;
    if (String(options.password || '').trim()) return true;
    if (options.expiresAt) return true;
    if ((Number(options.maxDownloads) || 0) > 0) return true;
    if (Array.isArray(options.memberUids) && options.memberUids.length > 0) return true;
    return false;
}

/**
 * @param {object} share
 * @param {{ nowMs?: number, uid?: string }} [ctx]
 * @returns {{ status: string }}
 */
export function evaluateDriveShareAccess(share, ctx = {}) {
    if (!share || share.revoked) return { status: 'revoked' };
    const nowMs = Number(ctx.nowMs) || Date.now();
    const expiresMs = toMillis(share.expiresAt);
    if (expiresMs > 0 && expiresMs <= nowMs) return { status: 'expired' };
    const max = Number(share.maxDownloads) || 0;
    if (max > 0 && (Number(share.downloadCount) || 0) >= max) {
        return { status: 'exhausted' };
    }
    const members = Array.isArray(share.memberUids)
        ? share.memberUids.filter(Boolean)
        : [];
    if (share.kind === 'internal' && members.length > 0) {
        if (!ctx.uid || !members.includes(ctx.uid)) return { status: 'forbidden' };
    }
    if (share.passwordProtected) return { status: 'need_password' };
    return { status: 'ok' };
}

function bytesToHex(bytes) {
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function createDriveShareSalt() {
    const bytes = new Uint8Array(16);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(bytes);
    } else {
        for (let i = 0; i < bytes.length; i += 1) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
    }
    return bytesToHex(bytes);
}

/**
 * @param {string} password
 * @param {string} [saltHex]
 * @returns {Promise<{ salt: string, hash: string }>}
 */
export async function hashDriveSharePassword(password, saltHex) {
    const salt = String(saltHex || '').trim() || createDriveShareSalt();
    const material = `${salt}:${String(password || '')}`;
    if (!globalThis.crypto?.subtle) {
        throw new Error('Password protection needs a modern browser.');
    }
    const buf = await globalThis.crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(material)
    );
    return { salt, hash: bytesToHex(new Uint8Array(buf)) };
}

/**
 * @param {object} share
 * @param {string} password
 * @returns {Promise<boolean>}
 */
export async function verifyDriveSharePassword(share, password) {
    if (!share?.passwordProtected) return true;
    const salt = String(share.passwordSalt || '').trim();
    const expected = String(share.passwordHash || '').trim();
    if (!salt || !expected) return false;
    const { hash } = await hashDriveSharePassword(password, salt);
    return hash === expected;
}

/**
 * Strip Storage URLs and password material before showing a locked share.
 * @param {object} share
 */
export function redactDriveShareForGate(share) {
    if (!share) return share;
    const files = Array.isArray(share.files)
        ? share.files.map((file) => ({
            itemId: file.itemId,
            fileName: file.fileName,
            contentType: file.contentType,
            sizeBytes: file.sizeBytes,
        }))
        : [];
    return {
        ...share,
        downloadUrl: '',
        storagePath: '',
        passwordHash: '',
        passwordSalt: '',
        files,
    };
}
